import { useState } from 'react';
import { Building2 } from 'lucide-react';
import { cn } from '@/lib/utils';

interface SafeImageFallbackProps {
  src: string | null | undefined;
  alt: string;
  className?: string;
  fallbackClassName?: string;
  containerClassName?: string;
  fallbackSize?: 'sm' | 'md' | 'lg';
  /** Reserves the rendered image box so lazy identity marks never shift a table row. */
  width?: number;
  height?: number;
}

/**
 * SafeImageFallback - A secure image component that shows a fallback icon
 * when the image fails to load. This avoids using innerHTML which could
 * potentially be exploited for XSS attacks.
 */
export function SafeImageFallback({
  src,
  alt,
  className,
  fallbackClassName,
  containerClassName,
  fallbackSize = 'md',
  width,
  height,
}: SafeImageFallbackProps) {
  // Track the src that failed to load rather than a bare boolean. When `src`
  // later changes (e.g. an async marketplace-listing / discovered-logo lookup
  // replaces a stale `supplier.logo_url` — ENT-3261) the derived `hasError`
  // clears automatically and the new URL is retried, instead of latching the
  // Building2 fallback until the component is remounted.
  const [erroredSrc, setErroredSrc] = useState<string | null>(null);
  const hasError = erroredSrc !== null && erroredSrc === src;

  const sizeClasses = {
    sm: 'h-4 w-4',
    md: 'h-5 w-5',
    lg: 'h-8 w-8',
  };

  if (!src || hasError) {
    return (
      <div className={cn('flex items-center justify-center', containerClassName)}>
        <Building2 className={cn(sizeClasses[fallbackSize], 'text-primary', fallbackClassName)} />
      </div>
    );
  }

  return (
    <img
      src={src}
      alt={alt}
      className={className}
      width={width}
      height={height}
      loading="lazy"
      decoding="async"
      onError={() => setErroredSrc(src ?? null)}
    />
  );
}
